import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import "@/App.css";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import HotelSignup from "@/pages/HotelSignup";
import Search from "@/pages/Search";
import MapSearch from "@/pages/MapSearch";
import PoolDetail from "@/pages/PoolDetail";
import OwnerDashboard from "@/pages/OwnerDashboard";
import GuestDashboard from "@/pages/GuestDashboard";
import CreateListing from "@/pages/CreateListing";
import AvailabilityManager from "@/pages/AvailabilityManager";
import PaymentResult from "@/pages/PaymentResult";
import PayPalReturn from "@/pages/PayPalReturn";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import SuperAdmin from "@/pages/SuperAdmin";
import Reactivate from "@/pages/Reactivate";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Support from "@/pages/Support";
import Favorites from "@/pages/Favorites";
import { ShieldAlert } from "lucide-react";

function Maintenance() {
  const { t, user, logout } = useApp();
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#F9F8F6]">
      <div className="max-w-md w-full bg-white border border-border rounded-2xl p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-[#E27B58]/15 flex items-center justify-center mx-auto mb-5"><ShieldAlert className="w-7 h-7 text-[#E27B58]" /></div>
        <h1 className="font-display text-3xl font-bold mb-2">{t("maintenance.title")}</h1>
        <p className="text-muted-foreground mb-6">{t("maintenance.subtitle")}</p>
        {user?.role === "admin" ? (
          <Link to="/admin" className="btn-primary inline-flex" data-testid="maintenance-admin-in">{t("maintenance.adminEnter")}</Link>
        ) : (
          <div className="space-y-3">
            <Link to="/auth" className="btn-primary inline-flex" data-testid="maintenance-admin-login">{t("maintenance.adminLogin")}</Link>
            {user && <button onClick={logout} className="block mx-auto text-sm text-muted-foreground hover:underline">{t("nav.logout")}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { config, user, loading } = useApp();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  // Maintenance mode: block everyone except admin (admin still routes via /admin + /auth)
  if (!loading && config?.maintenance && user?.role !== "admin" && !["/auth", "/admin"].some((p) => location.pathname.startsWith(p))) {
    return <Maintenance />;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/register/hotel" element={<HotelSignup />} />
        <Route path="/search" element={<Search />} />
        <Route path="/map" element={<MapSearch />} />
        <Route path="/pool/:id" element={<PoolDetail />} />
        <Route path="/owner" element={<OwnerDashboard />} />
        <Route path="/owner/new" element={<CreateListing />} />
        <Route path="/owner/edit/:id" element={<CreateListing />} />
        <Route path="/owner/availability/:id" element={<AvailabilityManager />} />
        <Route path="/guest" element={<GuestDashboard />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/poolpass-hq-control" element={<SuperAdmin />} />
        <Route path="/reactivate" element={<Reactivate />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/support" element={<Support />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/payment/success" element={<PaymentResult status="success" />} />
        <Route path="/payment/cancel" element={<PaymentResult status="cancel" />} />
        <Route path="/payment/paypal-return" element={<PayPalReturn />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}
